import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { SETTINGS_SHADOW_SM } from './settingsStyles';

export const teamTabStyles = StyleSheet.create({
	container: {
		maxWidth: 760,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 16,
	},
	card: {
		// Routing card sits above the teammates card; the section header owns the top margin.
	},

	// ---- Sub-headings inside a card ----
	cardSubTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: 15,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	routingTitleSpacing: {
		marginBottom: 16,
	},

	permissionsInfo: {
		minWidth: 0,
	},
	permissionsTitleRow: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: 8,
	},
	restrictedBadge: {
		backgroundColor: THEME.colors.background.dark,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 4,
		color: THEME.colors.text.light,
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.04em',
		padding: '2px 6px',
		textTransform: 'uppercase',
	},
	permissionsButton: {
		flexShrink: 0,
		marginLeft: 'auto',
		whiteSpace: 'nowrap',
	},
	permissionsDescription: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		lineHeight: 1.45,
		marginTop: THEME.spacing.xs,
	},
	permissionsSummary: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
		lineHeight: 1.45,
		marginTop: THEME.spacing.sm,
	},

	// ---- Routing toggle row ----
	toggleRowSpaced: {
		marginBottom: 4,
	},

	// ---- Setting rows (label/desc on the left, control on the right) ----
	settingRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		padding: '14px 0 0',
		marginTop: 14,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	settingInfo: {
		minWidth: 0,
	},
	settingTitle: {
		fontSize: 14,
		fontWeight: 500,
		color: THEME.colors.text.main,
	},
	settingDesc: {
		fontSize: 12,
		color: THEME.colors.text.light,
		marginTop: 2,
		lineHeight: 1.45,
		maxWidth: 420,
	},

	// ---- Segmented control ----
	segmented: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		backgroundColor: THEME.colors.background.dark,
		borderRadius: 9,
		padding: 3,
		flex: 'none',
	},
	segmentedBtn: {
		height: 'auto',
		padding: '6px 14px',
		border: 'none',
		borderRadius: 7,
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: 400,
		background: 'transparent',
		color: THEME.colors.text.light,
	},
	segmentedBtnActive: {
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.main,
		boxShadow: SETTINGS_SHADOW_SM,
		fontWeight: 500,
	},

	numberInput: {
		width: 80,
		height: 40,
		padding: '0 13px',
		fontSize: 14,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: SETTINGS_SHADOW_SM,
		outline: 'none',
		textAlign: 'center' as const,
		boxSizing: 'border-box' as const,
		flex: 'none',
		':focus': {
			borderColor: THEME.colors.primary.main,
		},
	},

	saveRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		marginTop: 20,
	},
	successMsg: {
		fontSize: 13,
		color: THEME.colors.success.dark,
	},
	errorMsg: {
		fontSize: 13,
		color: THEME.colors.danger.main,
		marginTop: 4,
	},

	// ---- Small agent toggle: size overrides applied on top of settingsStyles' base toggle ----
	toggleSmall: {
		width: 36,
		height: 20,
		borderRadius: 10,
	},
	toggleKnobSmall: {
		width: 16,
		height: 16,
	},
	toggleKnobSmallActive: {
		left: 18,
	},

	// ---- Teammates ----
	teammatesHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 14,
	},
	teammatesTitleRow: {
		display: 'flex',
		alignItems: 'baseline',
		gap: 8,
	},
	teammatesCount: {
		fontSize: 13,
		color: THEME.colors.text.light,
	},
	addTeammateButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
	},
	addForm: {
		padding: 16,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
		marginBottom: 16,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 10,
	},
	searchDropdown: {
		position: 'absolute' as const,
		top: '100%',
		left: 0,
		right: 0,
		marginTop: 4,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
		zIndex: 100,
		maxHeight: 240,
		overflowY: 'auto' as const,
	},
	searchItem: {
		padding: '8px 12px',
		cursor: 'pointer',
		':hover': {
			backgroundColor: THEME.colors.background.main,
		},
	},
	addFormActions: {
		display: 'flex',
		gap: 8,
	},
	agentList: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: '0',
	},
	emptyState: {
		padding: 24,
		textAlign: 'center' as const,
		color: THEME.colors.text.light,
		fontSize: 14,
	},
	agentRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '11px 0',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	agentInfo: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	avatarSmall: {
		width: 28,
		height: 28,
	},
	agentName: {
		fontSize: 13,
		fontWeight: 500,
		color: THEME.colors.text.main,
	},
	agentControls: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	availabilityLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		cursor: 'pointer',
	},
	availabilityText: {
		fontSize: 13,
		color: THEME.colors.text.light,
		// Reserve the width of the longest label ("Unavailable") so the controls line up in
		// columns across rows instead of drifting with each row's label width.
		minWidth: 78,
	},
	roleSelectTrigger: {
		width: 104,
	},
	searchWrapper: {
		position: 'relative' as const,
	},
	searchItemMuted: {
		color: THEME.colors.text.light,
	},
	searchResultRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	searchResultName: {
		fontWeight: 500,
		fontSize: 14,
	},
	searchResultEmail: {
		fontSize: 12,
		color: THEME.colors.text.light,
	},
	numberInputSmall: {
		width: 64,
		height: 34,
		padding: '0 8px',
		fontSize: 13,
	},
	gitHubLoginCell: {
		height: 26,
		minWidth: 0,
		padding: '0 6px',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
		...THEME.common.truncate,
	},
	gitHubLoginCellEmpty: {
		opacity: 0.7,
	},
	gitHubLoginInput: {
		width: 150,
		height: 26,
		padding: '0 6px',
		fontSize: 13,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: SETTINGS_SHADOW_SM,
		outline: 'none',
		boxSizing: 'border-box' as const,
	},
});
